import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { SaludFiscalHero } from "@/pages/estado-fiscal/SaludFiscalHero";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getFiscalAgenda,
  getFiscalHealth,
  type FiscalAgendaObligation,
} from "@/services/fiscalService";
import {
  type HealthBand,
  type HealthScoreBreakdown,
} from "@/pages/dashboard/fiscalHealthScore";
import { flattenAgenda } from "@/pages/dashboard/dashboardAlertsModel";
import { FiscalHealthExplanationModal } from "@/pages/dashboard/FiscalHealthExplanationModal";
import { useEffectiveHealthBreakdown } from "@/hooks/useEffectiveHealthBreakdown";
import { parseLocalDateOnly } from "@/lib/dateFormat";

function scoreToBand(score: number): HealthBand {
  if (score >= 80) return "healthy";
  if (score >= 50) return "needsAttention";
  return "atRisk";
}

interface DashboardFiscalHealthScoreProps {
  /**
   * "hero" is the advanced view's full SaludFiscalHero. "mini" is the simple
   * view's compact white card, kept visually subordinate to the dark month
   * summary above it (mirrors the mobile Inicio hierarchy).
   */
  variant?: "hero" | "mini";
}

/**
 * Dashboard "Salud fiscal" hero. Renders the same SaludFiscalHero used on the
 * estado-fiscal page so the score reads consistently across both surfaces.
 * The "Ver estado fiscal explicado" link opens the explanation modal in-place
 * — the previous behavior (navigate to /estado-fiscal) dropped the user on
 * the list page without ever showing the explanation they clicked for.
 *
 * THE SCORE COMES FROM THE BACKEND, ONLY. GET /fiscal-health reads persisted
 * obligations, whose status records what the tenant actually FILED. The portal
 * has no filing state, so it cannot compute this number — it previously tried,
 * via a client-side computeHealthScore built on calendar-generated deadlines,
 * and every one of those counted as overdue. That fallback rendered 0/100
 * "Atención requerida" to a flawless tenant on every load until the request
 * resolved. There is deliberately no client-side fallback now: while the
 * request is in flight we show a skeleton, and if it fails we say so rather
 * than inventing a number.
 */
export function DashboardFiscalHealthScore({
  variant = "hero",
}: DashboardFiscalHealthScoreProps = {}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [healthModalOpen, setHealthModalOpen] = useState(false);

  const {
    data: fiscalHealth,
    isPending: fiscalHealthPending,
    isError: fiscalHealthFailed,
  } = useQuery({
    queryKey: ["fiscal-health"],
    queryFn: getFiscalHealth,
  });

  // Pull the same agenda window the estado-fiscal page uses so the modal can
  // list specific overdue / due-soon obligations, and so we can reconcile the
  // local agenda counts against the API score via useEffectiveHealthBreakdown.
  const year = new Date().getFullYear();
  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", `${year}-01`, `${year}-12`],
    queryFn: () => getFiscalAgenda(`${year}-01`, `${year}-12`),
  });
  const allObligations = useMemo(
    () => flattenAgenda(agendaGrouped),
    [agendaGrouped],
  );

  const overdueObligations = useMemo<FiscalAgendaObligation[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return allObligations.filter((o) => {
      if (o.status === "completed") return false;
      return parseLocalDateOnly(o.dueDate) < today;
    });
  }, [allObligations]);
  const dueTodayObligations = useMemo<FiscalAgendaObligation[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return allObligations.filter((o) => {
      if (o.status === "completed") return false;
      return parseLocalDateOnly(o.dueDate).getTime() === today.getTime();
    });
  }, [allObligations]);
  const dueIn1Obligations = useMemo<FiscalAgendaObligation[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return allObligations.filter((o) => {
      if (o.status === "completed") return false;
      return parseLocalDateOnly(o.dueDate).getTime() === tomorrow.getTime();
    });
  }, [allObligations]);
  const dueIn3Obligations = useMemo<FiscalAgendaObligation[]>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const in3 = new Date(today);
    in3.setDate(in3.getDate() + 3);
    return allObligations.filter((o) => {
      if (o.status === "completed") return false;
      const due = parseLocalDateOnly(o.dueDate);
      return due > tomorrow && due <= in3;
    });
  }, [allObligations]);

  const baseBreakdown = useMemo<HealthScoreBreakdown>(
    () => ({
      score: fiscalHealth?.score ?? 0,
      band: scoreToBand(fiscalHealth?.score ?? 0),
      penalties: {
        overdue: fiscalHealth?.factors.overdueCount ?? 0,
        dueIn7Days: fiscalHealth?.factors.dueIn3Count ?? 0,
        noCae: 0,
        caeLow: 0,
        noInvoicesThisMonth: 0,
      },
      fiscalApiFactors: fiscalHealth?.factors,
    }),
    [fiscalHealth],
  );

  const breakdown = useEffectiveHealthBreakdown(
    baseBreakdown,
    overdueObligations,
    dueTodayObligations,
    dueIn1Obligations,
    dueIn3Obligations,
  );
  const score = breakdown.score;
  const band = breakdown.band;
  const isHealthy = band === "healthy";
  const statusLead = isHealthy
    ? t("estadoFiscal.heroStatusOkLead", "Sin problemas")
    : band === "needsAttention"
      ? t("estadoFiscal.heroStatusWarnLead", "Algunos puntos")
      : t("estadoFiscal.heroStatusAtRiskLead", "Atención");
  const statusAccent = isHealthy
    ? t("estadoFiscal.heroStatusOkAccent", "detectados.")
    : band === "needsAttention"
      ? t("estadoFiscal.heroStatusWarnAccent", "para revisar.")
      : t("estadoFiscal.heroStatusAtRiskAccent", "requerida.");

  const monthOnlyFmt = new Intl.DateTimeFormat(i18n.language, { month: "long" });
  const monthName = monthOnlyFmt.format(new Date());
  const periodLabel = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${new Date().getFullYear()}`;

  // Short copy for the dashboard's lite hero — the long version lives on the
  // estado-fiscal page and would force this card to grow taller than its row sibling.
  const description = isHealthy
    ? t("estadoFiscal.heroDescOkLite", "Todo en orden este mes.")
    : t(
        "estadoFiscal.heroDescNeedsAttentionLite",
        "Hay puntos para revisar este mes.",
      );

  // The request is in flight and we have nothing to show. A skeleton, not a
  // zero — a rendered 0/100 reads as a verdict on the tenant.
  if (fiscalHealthPending) {
    return (
      <div
        className="rounded-lg border border-[var(--fx-ink-200)] p-5"
        aria-busy="true"
        aria-label={t("dashboard.fiscalHealth.loading", "Calculando tu estado fiscal…")}
      >
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-4 h-10 w-24" />
        <Skeleton className="mt-3 h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-2/3" />
      </div>
    );
  }

  // The request failed. Say so: the alternative is a number the portal cannot
  // stand behind, and a wrong "todo en orden" is worse than an honest gap.
  if (fiscalHealthFailed || !fiscalHealth) {
    return (
      <div
        className="rounded-lg border border-[var(--fx-ink-200)] p-5"
        role="status"
      >
        <p className="text-sm font-medium text-[var(--fx-ink-900)] dark:text-[var(--fx-ink-100)]">
          {t("dashboard.fiscalHealth.errorTitle", "No pudimos calcular tu estado fiscal")}
        </p>
        <p className="mt-2 text-sm text-[var(--fx-ink-600)] dark:text-[var(--fx-ink-400)]">
          {t(
            "dashboard.fiscalHealth.errorBody",
            "Volvé a cargar la página en unos minutos. Tu agenda fiscal sigue disponible.",
          )}
        </p>
      </div>
    );
  }

  return (
    <>
      {variant === "mini" ? (
        // Simple view. The full hero is a large dark gradient card with a
        // ~110px score, which sat directly under the dark month-summary hero
        // and competed with it for the eye. Mobile deliberately makes this a
        // small white card subordinate to that hero; this mirrors it.
        <button
          type="button"
          className="fx-health-mini"
          onClick={() => setHealthModalOpen(true)}
          aria-label={t(
            "dashboard.fiscalHealth.miniAria",
            "Salud fiscal {{score}} de 100. Ver el detalle.",
            { score },
          )}
        >
          <span className="fx-health-mini-eyebrow">
            {t("dashboard.fiscalHealth.miniTitle", "Salud fiscal")}
          </span>
          <span className="fx-health-mini-row">
            <span className="fx-health-mini-score">
              <span className={`val band-${band}`}>{score}</span>
              <span className="max">/ 100</span>
            </span>
            <span className={`fx-health-mini-status band-${band}`}>
              {statusLead} <span className="accent">{statusAccent}</span>
            </span>
          </span>
        </button>
      ) : (
        <SaludFiscalHero
          score={score}
          indicatorsChecked={score}
          statusLead={statusLead}
          statusAccent={statusAccent}
          periodLabel={periodLabel}
          description={description}
          explainHref={() => setHealthModalOpen(true)}
          compact
          lite
        />
      )}
      <FiscalHealthExplanationModal
        open={healthModalOpen}
        onOpenChange={setHealthModalOpen}
        breakdown={breakdown}
        overdueObligations={overdueObligations}
        dueTodayObligations={dueTodayObligations}
        dueIn1Obligations={dueIn1Obligations}
        dueIn3Obligations={dueIn3Obligations}
      />
    </>
  );
}
